import { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Alert } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EasyMDE from "easymde";
import "easymde/dist/easymde.min.css";

const DocumentationEditor = ({ value, onChange, onFileUpload }) => {
  const editorRef = useRef(null);
  const [editor, setEditor] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;

    const mdeInstance = new EasyMDE({
      element: editorRef.current,
      initialValue: value || "",
      spellChecker: false,
      autoDownloadFontAwesome: true,
      toolbar: [
        "bold",
        "italic",
        "heading",
        "|",
        "quote",
        "unordered-list",
        "ordered-list",
        "|",
        "link",
        "image",
        "table",
        "|",
        "preview",
        "side-by-side",
        "fullscreen",
        "|",
        "guide",
      ],
      previewRender: (plainText) => plainText,
      renderingConfig: {
        markedOptions: {
          breaks: true,
        },
      },
    });

    // Update parent component when editor changes
    mdeInstance.codemirror.on("change", () => {
      onChange(mdeInstance.value());
    });

    setEditor(mdeInstance);

    return () => {
      mdeInstance.toTextArea();
    };
  }, []);

  // Update editor value when prop changes (for loading existing docs)
  useEffect(() => {
    if (editor && value !== undefined && editor.value() !== value) {
      editor.value(value);
    }
  }, [value, editor]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith(".md") && file.type !== "text/plain") {
      alert("Please upload a markdown (.md) or text file");
      return;
    }

    try {
      const content = await file.text();
      onChange(content);
      if (editor) {
        editor.value(content);
      }

      // Notify parent component of file upload
      if (onFileUpload) {
        onFileUpload({
          fileName: file.name,
          size: file.size,
          uploadedAt: new Date(),
        });
      }
    } catch (error) {
      console.error("Error reading file:", error);
      alert("Error reading file. Please try again.");
    }

    // Reset input
    event.target.value = "";
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload Markdown File
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.txt"
          hidden
          onChange={handleFileUpload}
        />
      </Stack>

      <Box
        sx={{
          "& .editor-preview-side, & .editor-preview, & .CodeMirror-fullscreen":
            {
              borderRadius: "4px",
            },
          "& .CodeMirror": {
            borderRadius: "4px 4px 0 0",
            border: "1px solid #ccc",
          },
          "& .editor-toolbar": {
            borderRadius: "4px 4px 0 0",
            border: "1px solid #ccc",
          },
        }}
      >
        <textarea ref={editorRef} />
      </Box>
    </Box>
  );
};

export default DocumentationEditor;
